import type { Role } from '@af/shared';
import { passkeyClient } from '@better-auth/passkey/client';
import { adminClient, organizationClient, twoFactorClient } from 'better-auth/client/plugins';
import { createAuthClient } from 'better-auth/react';
import { call } from '@/lib/api';
import type { AuthAdapter, InvitationInfo, MemberInfo, PasskeyInfo } from './types';

export const authClient = createAuthClient({
  basePath: '/api/auth',
  plugins: [organizationClient(), twoFactorClient(), passkeyClient(), adminClient()],
});

type Res<T> =
  | { data: T | null; error: { message?: string; status?: number } | null }
  | { data: T; error: null };

async function unwrap<T>(p: Promise<unknown>): Promise<T> {
  const r = (await p) as Res<T>;
  if (r.error) throw new Error(r.error.message || `Auth error (${r.error.status ?? '?'})`);
  return r.data as T;
}

const iso = (d: unknown) => (d instanceof Date ? d.toISOString() : String(d ?? ''));

export const betterAuthAdapter: AuthAdapter = {
  async getSession() {
    const r = (await authClient.getSession()) as Res<{
      user: { id: string; email: string; name: string; twoFactorEnabled?: boolean | null };
      session: { id: string; activeOrganizationId?: string | null };
    }>;
    if (r.error || !r.data) return null;
    return {
      user: { ...r.data.user, twoFactorEnabled: Boolean(r.data.user.twoFactorEnabled) },
      session: { id: r.data.session.id, activeOrganizationId: r.data.session.activeOrganizationId ?? null },
    };
  },
  async signUpEmail(input) {
    const { setupToken, ...body } = input;
    await unwrap(
      authClient.signUp.email(body, setupToken ? { headers: { 'x-setup-token': setupToken } } : undefined),
    );
  },
  async signInEmail(input) {
    const data = await unwrap<{ twoFactorRedirect?: boolean }>(authClient.signIn.email(input));
    return data?.twoFactorRedirect ? { status: 'two_factor' } : { status: 'ok' };
  },
  async verifyTotp(input) {
    await unwrap(authClient.twoFactor.verifyTotp(input));
  },
  async sendEmailCode() {
    await unwrap(authClient.twoFactor.sendOtp());
  },
  async verifyEmailCode(input) {
    await unwrap(authClient.twoFactor.verifyOtp(input));
  },
  async signInPasskey() {
    await unwrap(authClient.signIn.passkey());
  },
  async signOut() {
    await unwrap(authClient.signOut());
  },
  async enableTotp(password) {
    return unwrap<{ totpURI: string; backupCodes: string[] }>(authClient.twoFactor.enable({ password }));
  },
  async disableTotp(password) {
    await unwrap(authClient.twoFactor.disable({ password }));
  },
  async listPasskeys() {
    const rows = await unwrap<
      { id: string; name?: string | null; createdAt: unknown; deviceType?: string }[]
    >(authClient.passkey.listUserPasskeys());
    return (rows ?? []).map(
      (p): PasskeyInfo => ({
        id: p.id,
        name: p.name ?? null,
        createdAt: iso(p.createdAt),
        deviceType: p.deviceType,
      }),
    );
  },
  async addPasskey(name) {
    await unwrap(authClient.passkey.addPasskey({ name }));
  },
  async deletePasskey(id) {
    await unwrap(authClient.passkey.deletePasskey({ id }));
  },
  // Served by the API (not Better Auth's list-sessions, which demands a session signed in within a day).
  async listSessions() {
    return call('listMySessions');
  },
  async revokeSession(id) {
    await call('revokeMySession', { params: { id } });
  },
  async listOrganizations() {
    const rows = await unwrap<{ id: string; name: string; slug: string }[]>(authClient.organization.list());
    return (rows ?? []).map((o) => ({ id: o.id, name: o.name, slug: o.slug }));
  },
  async createOrganization(input) {
    const o = await unwrap<{ id: string; name: string; slug: string }>(authClient.organization.create(input));
    return { id: o.id, name: o.name, slug: o.slug };
  },
  async setActiveOrganization(id) {
    await unwrap(authClient.organization.setActive({ organizationId: id }));
  },
  async updateOrganization(input) {
    await unwrap(authClient.organization.update({ data: input }));
  },
  async listMembers() {
    const org = await unwrap<{
      members: {
        id: string;
        userId: string;
        role: string;
        createdAt: unknown;
        user: { id: string; name: string; email: string };
      }[];
      invitations: { id: string; email: string; role: string; status: string; expiresAt: unknown }[];
    } | null>(authClient.organization.getFullOrganization());
    return {
      members: (org?.members ?? []).map(
        (m): MemberInfo => ({ ...m, role: m.role as Role, createdAt: iso(m.createdAt) }),
      ),
      invitations: (org?.invitations ?? []).map(
        (i): InvitationInfo => ({ ...i, role: i.role as Role, expiresAt: iso(i.expiresAt) }),
      ),
    };
  },
  async inviteMember(input) {
    await unwrap(authClient.organization.inviteMember({ email: input.email, role: input.role as 'admin' }));
  },
  async updateMemberRole(input) {
    await unwrap(
      authClient.organization.updateMemberRole({ memberId: input.memberId, role: input.role as 'admin' }),
    );
  },
  async removeMember(memberIdOrEmail) {
    await unwrap(authClient.organization.removeMember({ memberIdOrEmail }));
  },
  async cancelInvitation(invitationId) {
    await unwrap(authClient.organization.cancelInvitation({ invitationId }));
  },
};
